import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import type { Cart } from '@getjust/gateway';

import { loyaltyDiscountCodeAtom, loyaltyRedeemedAtom } from '$business/atoms/loyalty';
import { CART_QUERY_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { gateway } from '$src/http';
import { DeleteDiscountInput } from '$src/pages/api/[shopId]/[cartId]/delete-discount';

const MUTATION_DELETE_DISCOUNT_KEY = 'MUTATION/DELETE_DISCOUNT';

export const useDeleteDiscount = () => {
  const baseUrl = useBaseApiUrl();
  const queryClient = useQueryClient();
  const updateCartId = useCartIdUpdater();
  const { track } = useTrackAnalytics();
  const loyaltyDiscountCode = useAtomValue(loyaltyDiscountCodeAtom);
  const setLoyaltyRedeemed = useSetAtom(loyaltyRedeemedAtom);
  const setLoyaltyDiscountCode = useSetAtom(loyaltyDiscountCodeAtom);

  return useMutation({
    mutationFn: (discount: DeleteDiscountInput) => gateway.post<Cart>(`${baseUrl}/delete-discount`, discount),
    mutationKey: [MUTATION_DELETE_DISCOUNT_KEY, baseUrl],
    onSuccess: ({ data }, variables) => {
      track(AnalyticsEventName.DISCOUNT_CODE_DELETE_SUCCEEDED, { code: variables.code });
      updateCartId(data.id);
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data.id], data);

      if (loyaltyDiscountCode && variables.code === loyaltyDiscountCode) {
        setLoyaltyRedeemed(false);
        setLoyaltyDiscountCode(undefined);
        queryClient.invalidateQueries({ queryKey: ['QUERY/LOYALTY_BALANCE'] });
      }
    },
    onError: (_, variables) => {
      track(AnalyticsEventName.DISCOUNT_CODE_DELETE_FAILED, { code: variables.code });
    },
  });
};
